import * as Sentry from '@sentry/react'
import type { ErrorInfo } from 'react'

export interface TelemetryContext {
  /** Name of the error boundary or catch site */
  boundary?: string
  /** Current URL pathname (auto-populated when not provided) */
  route?: string
  /** Supabase tenant ID if available */
  tenantId?: string
  /** Supabase user ID if available */
  userId?: string
  /** Any additional structured context */
  extra?: Record<string, unknown>
}

/**
 * Capture an error with rich context tags to Sentry.
 *
 * Safe to call even when Sentry is not initialized (dev without DSN) —
 * @sentry/react silently no-ops uninitialized captures, so this never crashes.
 *
 * Always logs to console in dev so errors are visible locally regardless of DSN.
 */
export function captureError(error: unknown, context: TelemetryContext = {}): void {
  const route =
    context.route ??
    (typeof window !== 'undefined' ? window.location.pathname : undefined)

  if (import.meta.env.DEV) {
    console.error(
      `[telemetry] ${context.boundary ?? 'unknown'} at ${route ?? '?'}`,
      error,
      context,
    )
  }

  Sentry.withScope((scope) => {
    if (context.boundary) scope.setTag('boundary', context.boundary)
    if (route) scope.setTag('route', route)
    if (context.tenantId) scope.setTag('tenant_id', context.tenantId)
    if (context.userId) scope.setTag('user_id', context.userId)
    if (context.extra) scope.setExtras(context.extra)

    Sentry.captureException(error)
  })
}

/**
 * Convenience wrapper for React class-component error boundaries.
 * Automatically includes componentStack and current pathname.
 */
export function captureComponentError(
  error: Error,
  info: ErrorInfo,
  boundaryName: string,
  ctx?: Pick<TelemetryContext, 'tenantId' | 'userId'>,
): void {
  captureError(error, {
    boundary: boundaryName,
    tenantId: ctx?.tenantId,
    userId: ctx?.userId,
    extra: {
      componentStack: info.componentStack ?? undefined,
    },
  })
}
